import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button, showSnackbar } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { Language, usePermission } from "imports/Utils";

import {
	GetInvoiceGroupTypesDocument,
	GetInvoiceGroupTypesQuery,
} from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.queries.generated";
import {
	DeleteInvoiceGroupTypeDocument,
	UpdateInvoiceGroupTypeOrderDocument,
} from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.mutations.generated";
import { ChildPropsFromArray } from "types/commons";
import InvoiceGroupTypeSettingsDialog from "view/invoicegrouptypes/components/dialogs/InvoiceGroupTypeSettingsDialog";
import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";
import { Box } from "@mui/material";

type InvoiceGroupTypeFromQuery = ChildPropsFromArray<GetInvoiceGroupTypesQuery, "invoiceGroupTypes">;

const InvoiceGroupTypes = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const [ content, setContent ] = useState<InvoiceGroupTypeFromQuery[]>([]);

	const { refetch, loading } = useQuery(GetInvoiceGroupTypesDocument, {
		onCompleted: (response) => {
			setContent(response.invoiceGroupTypes ?? []);
		},
	});

	const [ deleteInvoiceGroupType ] = useMutation(DeleteInvoiceGroupTypeDocument);
	const [ updateInvoiceGroupTypeOrder ] = useMutation(UpdateInvoiceGroupTypeOrderDocument);

	const { isRTR, isFinance, isReadOnly } = usePermission();

	const [ settingsDialogOpen, setSettingsDialogOpen ] = useState(false);

	const canManageSettings = (isRTR || isFinance) && !isReadOnly;
	const canReorder = (isRTR || isFinance) && !isReadOnly;

	const { tableProps, refetch: refetchTable, refetchAfterDelete } = useFrontendTable({
		id: TableId.InvoiceGroupTypes,
		paginationEnabled: false,
		content: content.map(x => ({ ...x, id: x.id ?? undefined })),
	});

	const onDelete = (row: InvoiceGroupTypeFromQuery) => {
		deleteInvoiceGroupType({
			variables: {
				id: row.id!,
			},
		}).then(() => {
			refetch();
			refetchAfterDelete();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
		});
	};

	const onSaveOrder = (newOrder: InvoiceGroupTypeFromQuery[]) => {
		updateInvoiceGroupTypeOrder({
			variables: {
				inputs: newOrder.map((invoiceGroupType, index) => ({
					id: invoiceGroupType.id,
					sequenceNumber: index,
				})),
			},
		}).then(() => {
			refetch();
			refetchTable(0);
			showSnackbar(intl.formatMessage({ id: "ADMINISTRATION_SAVED" }), { variant: "success" });
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("INVOICE_GROUP_TYPES")}
					<Helmet title={intl.formatMessage({ id: "INVOICE_GROUP_TYPES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{canManageSettings && (
						<>
							<Button variant="outlined" onClick={() => setSettingsDialogOpen(true)}>
								{Language("INVOICE_GROUP_TYPE_SETTINGS")}
							</Button>

							<Button onClick={() => navigate("create")}>
								{Language("ADD_BUTTON")}
							</Button>
						</>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					onReorder={canReorder ? (reordered) => {
						const newContent = [ ...reordered as InvoiceGroupTypeFromQuery[] ];
						setContent(newContent);
						onSaveOrder(newContent);
					} : undefined}
					contentLoading={loading}
					columns={[ {
						id: "name",
						title: intl.formatMessage({ id: "NAME" }),
					}, {
						id: "declarationAutoCreationCondition",
						title: intl.formatMessage({ id: "DECLARATION_AUTO_CREATION_CONDITION" }),
						render: (row: InvoiceGroupTypeFromQuery) => row.declarationAutoCreationCondition ? (
							intl.formatMessage({ id: `DECLARATION_AUTO_CREATION_CONDITION_${row.declarationAutoCreationCondition}` })
						) : undefined,
					}, {
						id: "charterTypes",
						title: intl.formatMessage({ id: "CHARTER_TYPE" }),
						render: (row: InvoiceGroupTypeFromQuery) =>
							<Box display="flex" gap={1}>
								{row.charterTypes.map(x => <CharterTypeTag type={x} key={x} />)}
							</Box>,
					} ]}
					rowActions={(isRTR || isFinance) && !isReadOnly ? [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDelete(row);
							});
						},
					} ] : undefined} />
			</Card>

			{canManageSettings && (
				<InvoiceGroupTypeSettingsDialog
					open={settingsDialogOpen}
					onClose={() => setSettingsDialogOpen(false)} />
			)}
		</React.Fragment>
	);
};

export default InvoiceGroupTypes;
